export interface BiotwinBasics {
  age: number;
  ageUnit: 'year' | 'month';
  dateOfBirth: string;
  gender: 'male' | 'female';
  height: number;
  heightUnit: 'cm' | 'in';
  weight: number;
  weightUnit: 'kg' | 'lbs';
}

export interface Biotwin extends Partial<BiotwinBasics> {
  biobookUpdatePhraseIds?: number[];
  createdAt: string;
  createdBy: number;
  createdByUserName: string;
  createdOrModifiedAt: string;
  id: number;
  isDemo: boolean;
  isSample: boolean;
  modifiedAt: string;
  modifiedBy: number;
  modifiedByUserName: string;
  name: string;
  note?: string;
  patientId?: number | string;
  targetAdditionalIds: number[];
  targetSubscriptionTypeIds: number[];
}

export interface Condition {
  conditionName: string;
  conditionTypeName: ConditionTypeName;
  drugId?: number;
  drugName?: string;
  id: number;
  isChildSelectionRestricted: boolean;
  isGroup: boolean;
  otherParentLabelIds: number[];
  parentLabelId: number | null;
  showOrder: number | null;
  synonymNames: string[];
}

export interface ConditionType {
  id: number;
  conditionTypeName: string;
}

export interface BiotwinCondition {
  conditionId: number;
  date: string | null;
  id: number;
  note: string | null;
  unitId: number | null;
  value: number | null;
}

export interface BiotwinScore {
  hasMissingInformation: boolean;
  missingConditions: Condition[];
  scoreId: number;
  scoreShortDescription: string;
  scoreLongDescription: string;
  scoreValue: number | string | null;
  scoreValueUnit: string | null;
}

export enum ConditionRelationType {
  Selected = 1,
  OptionalRelated = 2,
  RequiredRelated = 3,
  Suggested = 4,
  Parent = 5,
}

export interface RelevantCondition {
  conditionId: number;
  conditionName: string;
  conditionRelationType: ConditionRelationType;
  conditionTypeName: ConditionTypeName;
  drugName?: string;
  hideFromAssigned: boolean;
  hideFromBiotype: boolean;
  hideWhenNoPhrase: boolean;
  isChildSelectionRestricted: boolean;
  isGroup: boolean;
  isPatientSetting: boolean;
  isScoreBased: boolean;
  isTool: boolean;
  otherChildrenAutoAdded: boolean;
  parentLabelId: number | null;
  phraseCount: number;
  relatedConditionId: number | null;
  relatedConditionIds?: number[];
  relatedScoreIds?: number[];
  relatedPhraseId: number | null;
  showOrder: number | null;
}

export interface RelevantConditionRelations {
  childIds: number[];
  childList: RelevantCondition[];
  optionalRelatedIds: number[];
  optionalRelatedList: RelevantCondition[];
  relevantCondition: RelevantCondition;
  requiredRelatedIds: number[];
  requiredRelatedList: RelevantCondition[];
  suggestedIds: number[];
  suggestedList: RelevantCondition[];
}

export enum BiotwinHistoryType {
  Added = 1,
  Changed = 2,
  Removed = 3,
}

export interface BiotwinHistoryItem {
  conditionDate: string;
  conditionName: string;
  conditionNote: string;
  conditionTypeName: string;
  conditionUnitName: string;
  conditionValue: unknown;
  createdByName: string;
  createdByUserName: string;
  date: string;
  isPerformedBySystem: boolean;
  type: BiotwinHistoryType;
  userId: number;
}

export interface BiotwinHubUser {
  biotypeUserId: number;
  email: string;
  name: string;
  userId: number;
  username: string;
}

export enum ConditionTypeName {
  Allergy = 'Allergy/intolerance',
  Category = 'Category',
  Demographic = 'Demographic',
  Diagnoses = 'Diagnoses',
  FamilyHistory = 'Family history',
  Medications = 'Medications',
  PhysiologyAndLifeFactors = 'Physiology & Life Factors',
  Procedures = 'Procedures',
  SymptomsAndFindings = 'Symptoms & Findings',
  Tests = 'Tests',
}

export interface E2EPatientData {
  biotypeId?: number;
  info: {
    age: number | null;
    ageUnit: 'year' | 'month';
    gender: 'male' | 'female' | null;
    height: number | null;
    heightUnit: 'cm' | 'in';
    name: string;
    patientId: number;
    surname: string;
    weight: number | null;
    weightUnit: 'kg' | 'lbs';
  };
  originalInfo: Record<string, any>;
}

export interface AIResult {
  bioTypeId?: number;
  phraseIds: number[];
  rephrasedQuestion?: string;
  result: string;
}

export interface AIQuestion {
  id: number;
  question: string;
}

export interface BiotwinExportData {
  biotwin: Pick<
    Biotwin,
    'name' | 'dateOfBirth' | 'gender' | 'height' | 'heightUnit' | 'note' | 'weight' | 'weightUnit'
  >;
  biotwinConditions: Pick<BiotwinCondition, 'conditionId' | 'date' | 'note' | 'unitId' | 'value'>[];
  demographicConditionIds: number[];
  selectedConditionIds: number[];
  requiredRelatedConditionIds: number[];
}
